import { useState } from 'react';
import { Link } from 'react-router-dom';
import { siteInfo, categories, articles, faqItems } from '@/mocks/siteData';

function FAQAccordion() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-3">
            常见问题解答
          </h2>
          <p className="text-foreground-600 text-sm md:text-base">
            关于憋尿任务挑战，大家都在问这些问题
          </p>
        </div>

        <div className="space-y-3">
          {faqItems.map((item, idx) => (
            <div
              key={idx}
              className="bg-background-50 border border-background-200 rounded-lg overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-100 transition-colors"
              >
                <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                  {item.question}
                </span>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i
                    className={`ri-${
                      openIndex === idx ? 'subtract' : 'add'
                    }-line text-foreground-500 transition-transform duration-200`}
                  ></i>
                </div>
              </button>
              {openIndex === idx && (
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {item.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const featuredArticles = articles.slice(0, 6);

  return (
    <>
      <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20abstract%20geometric%20composition%20with%20soft%20flowing%20curves%20in%20terracotta%20and%20cream%20tones%2C%20gentle%20gradient%20background%20evoking%20wellness%20and%20body%20health%2C%20clean%20aesthetic%20with%20organic%20fluid%20shapes%2C%20serene%20medical%20wellness%20atmosphere%2C%20no%20text%2C%20no%20sharp%20edges%2C%20harmonious%20composition&width=1600&height=900&seq=hero-bladder-2026&orientation=landscape"
          alt="憋尿任务挑战全攻略"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>

        <div className="relative z-10 w-full px-4 md:px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50/20 backdrop-blur-sm text-background-50 text-xs md:text-sm mb-6">
              <i className="ri-shield-check-line"></i>
              <span>科学认知 · 安全挑战 · 健康第一</span>
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 mb-4 leading-tight">
              憋尿任务挑战全攻略
            </h1>
            <p className="text-sm md:text-lg text-background-50/80 mb-8 max-w-xl mx-auto leading-relaxed">
              {siteInfo.description}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/rules"
                className="w-full sm:w-auto px-6 py-3 rounded-md bg-accent-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
              >
                查看挑战规则
              </Link>
              <Link
                to="/category/bladder-health"
                className="w-full sm:w-auto px-6 py-3 rounded-md border border-background-50/40 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-background-50/10 transition-colors cursor-pointer"
              >
                了解膀胱健康
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-3">
              探索分类
            </h2>
            <p className="text-foreground-600 text-sm md:text-base">
              六大分类，全面覆盖憋尿任务相关知识
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group bg-background-50 rounded-lg p-5 md:p-6 border border-background-200 hover:border-accent-300 transition-all duration-200 cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4 group-hover:bg-accent-200 transition-colors">
                  <i className={`${cat.icon} text-lg`}></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 text-base mb-2 group-hover:text-accent-700 transition-colors">
                  {cat.name}
                </h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                  {cat.description}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-3 mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-2">
                精选文章
              </h2>
              <p className="text-foreground-600 text-sm">
                最新、最全面的憋尿任务知识文章
              </p>
            </div>
            <Link
              to="/category/rules"
              className="text-sm text-accent-600 hover:text-accent-700 font-medium whitespace-nowrap flex items-center gap-1 cursor-pointer"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {featuredArticles.map((article) => {
              const cat = categories.find((c) => c.slug === article.category);
              return (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-accent-300 transition-all duration-200 cursor-pointer flex flex-col"
                >
                  <div className="relative w-full h-44 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4 md:p-5 flex flex-col flex-1">
                    {cat && (
                      <span className="inline-block text-xs text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full mb-3 self-start whitespace-nowrap">
                        {cat.name}
                      </span>
                    )}
                    <h3 className="font-heading font-semibold text-foreground-900 text-sm md:text-base mb-2 line-clamp-2 group-hover:text-accent-700 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3 flex-1">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {article.readTime} 分钟阅读
                      </span>
                      <span>{article.publishedAt}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <FAQAccordion />

      <section className="py-16 md:py-20 relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Soft%20abstract%20fluid%20gradient%20in%20warm%20terracotta%20and%20peach%20tones%2C%20minimalist%20gentle%20curves%2C%20clean%20wellness%20brand%20background%2C%20no%20text%2C%20calm%20serene%20atmosphere&width=1600&height=600&seq=cta-newsletter-2026&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-black/50"></div>

        <div className="relative z-10 max-w-xl mx-auto text-center px-4 md:px-6">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500/20 mx-auto mb-5">
            <i className="ri-mail-send-line text-xl text-background-50"></i>
          </div>
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-background-50 mb-3">
            订阅我们的资讯
          </h2>
          <p className="text-sm text-background-50/70 mb-6 leading-relaxed">
            获取最新的憋尿任务知识、膀胱健康科普和挑战技巧，科学安全地享受挑战乐趣。
          </p>

          <form
            data-readdy-form
            action="https://readdy.ai/api/form/d9q50hknlsngrm4llrig"
            method="POST"
            className="flex flex-col sm:flex-row gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement);
              if (honeypot && honeypot.value.trim()) {
                const msg = document.getElementById('newsletter-msg');
                if (msg) { msg.textContent = '订阅成功！感谢您的关注。'; msg.className = 'text-green-300 text-sm mt-3'; }
                (form.querySelector('[name="email"]') as HTMLInputElement).value = '';
                return;
              }
              const formData = new FormData(form);
              const url = form.getAttribute('action') || '';
              fetch(url, { method: 'POST', body: new URLSearchParams(formData as any).toString(), headers: { 'Content-Type': 'application/x-www-form-urlencoded' } })
                .then(async (r) => {
                  const t = await r.text();
                  let parsed: any = {};
                  try { parsed = JSON.parse(t); } catch (_) { parsed = {}; }
                  const msg = document.getElementById('newsletter-msg');
                  if (!msg) return;
                  if (r.ok && parsed?.code === 'OK') {
                    msg.textContent = '订阅成功！感谢您的关注。';
                    msg.className = 'text-green-300 text-sm mt-3';
                    (form.querySelector('[name="email"]') as HTMLInputElement).value = '';
                  } else {
                    const errMsg = parsed?.meta?.message || parsed?.message || '订阅失败，请稍后再试。';
                    msg.textContent = errMsg;
                    msg.className = 'text-red-300 text-sm mt-3';
                  }
                })
                .catch(() => {
                  const msg = document.getElementById('newsletter-msg');
                  if (msg) { msg.textContent = '网络错误，请稍后再试。'; msg.className = 'text-red-300 text-sm mt-3'; }
                });
            }}
          >
            <input
              type="email"
              name="email"
              placeholder="输入您的邮箱地址"
              required
              className="flex-1 px-4 py-3 rounded-md bg-background-50/15 border border-background-50/30 text-background-50 placeholder:text-background-50/50 text-sm focus:outline-none focus:ring-1 focus:ring-accent-400"
            />
            <button
              type="submit"
              className="px-6 py-3 rounded-md bg-accent-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
            >
              立即订阅
            </button>
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="footer-honeypot"
            />
          </form>
          <div id="newsletter-msg" className="text-sm mt-3"></div>
        </div>
      </section>
    </>
  );
}